import React, { useState, useCallback, ReactNode, useEffect } from "react";
import { cn } from "@/lib/utils";
import ChevronLeftIcon from "@heroicons/react/24/solid/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/solid/ChevronRightIcon";
import debounce from "@/lib/debounce";
import { useTranslation } from "@/hooks/translationContext";

interface Props {
  itemIndex: number;
  setItemIndex: (index: number) => void;
  count: number;
  children: any;
  dotColor: "gray" | "white";
  showButtons: boolean;
  hoverable?: boolean;
  height?: string;
  aspectRatio?: string;
}

const Carousel = ({
  children,
  count,
  itemIndex,
  setItemIndex,
  dotColor,
  showButtons,
  hoverable = true,
  height,
  aspectRatio = "1/1",
}: Props) => {
  const { t } = useTranslation();
  const [pause, setPause] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const content = children;

  const getContent = () => {
    if (Array.isArray(content)) return content;
    if (Array.isArray(content?.props?.children))
      return content.props.children.flat();

    return [content?.props?.children || content];
  };

  const handleNext = useCallback(
    debounce(() => {
      if (itemIndex !== count - 1) {
        setItemIndex(itemIndex + 1);
      } else {
        setItemIndex(0);
      }
    }, 300),
    [itemIndex, count],
  );

  const handlePre = useCallback(
    debounce(() => {
      if (itemIndex !== 0) {
        setItemIndex(itemIndex - 1);
      } else {
        setItemIndex(count - 1);
      }
    }, 300),
    [itemIndex, count],
  );

  const handlePage = useCallback(
    debounce((page: number) => {
      setItemIndex(page);
    }, 300),
    [],
  );

  const handleMouse = () => {
    setPause(!pause);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touchX = e.targetTouches[0].clientX;
    const touchDiff = Math.abs(touchStart - touchX);
    
    // If horizontal swipe is detected, prevent page scrolling
    if (touchDiff > 10) {
      e.preventDefault();
      setIsSwiping(true);
    }
    
    setTouchEnd(touchX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isSwiping) return;

    e.preventDefault();
    
    if (touchStart - touchEnd > 50) {
      handleNext();
    } else if (touchStart - touchEnd < -50) {
      handlePre();
    }

    setIsSwiping(false);
  };

  return (
    <div className="flex justify-end w-full group">
      <div
        className="slide group w-full relative"
        //onMouseEnter={handleMouse}
        //onMouseLeave={handleMouse}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: 'pan-y',
          WebkitTouchCallout: 'none',
          userSelect: 'none'
        }}
      >
        <div
          className="relative w-full overflow-hidden"
          style={{
            height: height || undefined,
            aspectRatio: !height ? aspectRatio : undefined,
          }}
        >
          {Array.isArray(getContent()) &&
            getContent().map((item: ReactNode, index: number) => {
              return (
                <div
                  className={cn(
                    "absolute inset-0 w-full h-full transition-opacity duration-500 ease-in-out",
                    itemIndex === index
                      ? "opacity-100 z-10"
                      : "opacity-0 z-0 pointer-events-none"
                  )}
                  key={index}
                >
                  {item}
                </div>
              );
            })}
        </div>

        {showButtons && (
          <>
            <button
              aria-label={t("listpage-carousel-next-label")}
              onClick={() => handlePre()}
              className={`${showButtons ? "opacity-100 md:opacity-0 z-[9999]" : "opacity-0"} ${hoverable ? "group-hover:!opacity-100" : ""} duration-150 absolute items-center left-[8px] text-[12px] w-8 h-8 top-[50%] translate-y-[-50%] rounded-full flex justify-center bg-white text-primary shadow-md`}
            >
              <ChevronLeftIcon className="size-4" />
            </button>
            <button
              aria-label={t("listpage-carousel-previous-label")}
              onClick={() => handleNext()}
              className={`${showButtons ? "opacity-100 md:opacity-0 z-[9999]" : "opacity-0"} ${hoverable ? "group-hover:!opacity-100" : ""} duration-150 absolute items-center right-[8px] text-[12px] w-8 h-8 top-[50%] translate-y-[-50%] rounded-full flex justify-center bg-white text-primary shadow-md`}
            >
              <ChevronRightIcon className="size-4" />
            </button>
          </>
        )}
      </div>

      <div className="flex gap-2 z-10 items-center absolute w-full justify-center bottom-3">
        <PaginationButtons
          itemIndex={itemIndex}
          count={count}
          handlePage={handlePage}
          dotColor={dotColor}
        />
      </div>
    </div>
  );
};

const PaginationButtons = ({
  count,
  itemIndex,
  handlePage,
  dotColor,
}: {
  count: number;
  itemIndex: number;
  handlePage: (index: number) => void;
  dotColor: string;
}) => {
  const { t } = useTranslation();
  const maxButtons = 4;
  const buttons = Array.from(
    { length: Math.min(count, maxButtons) },
    (_, i) => {
      const pageIndex = (itemIndex + i) % count;
      return pageIndex;
    },
  );

  return (
    <>
      {buttons.map((_, index) => {
        const pageIndex =
          index + maxButtons * Math.floor(itemIndex / maxButtons);

        const borderColorClass =
          dotColor === "gray" ? "border-assistant" : "border-white";

        const currentIndexBackgroundColor =
          dotColor == "gray" ? "bg-primary" : "bg-white";
        const otherIndexBackgroundColor =
          dotColor == "gray" ? "bg-assistant" : "bg-transparent";
        const backgroundColorClass =
          itemIndex === pageIndex
            ? currentIndexBackgroundColor
            : otherIndexBackgroundColor;

        const sizeClass =
          count - 1 === pageIndex ? "w-1 h-1" : "w-[6px] h-[6px]";

        return (
          <div
            role="button"
            aria-label={t("listpage-carousel-dot-label")}
            key={index}
            onClick={() => handlePage(pageIndex)}
            className={cn(
              borderColorClass,
              "rounded-full border",
              backgroundColorClass,
              sizeClass,
            )}
          ></div>
        );
      })}
    </>
  );
};

export default Carousel;
